// src/components/Sidebar/SidebarHeader.tsx
"use client";

import { useAuthContext } from "@/contexts/AuthContext";
import { useStripePlanStatus } from "@/hooks/useStripePlanStatus";
import { useRecordContext } from "@/contexts/RecordContext";
import PlanLabel from "@/components/PlanLabel";
import { getEffectivePlan } from "@/lib/planMap";
import PlanCtaLink from "@/components/PlanCtaLink";

export default function SidebarHeader() {
  const { clerkUser } = useAuthContext();
  const { sidebarCollapsed, userProfile } = useRecordContext() || {};
  const { status: stripeStatus, loading: stripeLoading } = useStripePlanStatus();

  if (!clerkUser || sidebarCollapsed) return null;
  if (stripeLoading) return null;
  if (!userProfile && !stripeStatus) return null;

  const userLikeForLabel = {
    nazeka_member:
      userProfile?.nazeka_member ??
      stripeStatus?.nazeka_member ??
      false,
    user_plan:
      stripeStatus?.user_plan ??
      userProfile?.user_plan ??
      "pending",
    trialActive: stripeStatus?.trialActive ?? false,
    trialEnd: stripeStatus?.trialEnd ?? null,
    fullAccessUntil: (userProfile as any)?.entitlements?.full_access_until ?? null,
  };

  // Apply effective plan resolution so admin/operator bypass is reflected in display.
  // For admin (is_admin_bypass), use the server-resolved effective_plan directly so
  // the trial/entitlement "体験中" label is not incorrectly shown over the operator role.
  // For all other users, getEffectivePlan resolves from raw billing fields as before.
  const isAdminBypass = (userProfile as any)?.is_admin_bypass === true;
  const displayPlan = isAdminBypass
    ? ((userProfile as any)?.effective_plan as string ?? "discovery_pro")
    : getEffectivePlan({ ...userLikeForLabel, uid: clerkUser.id });

  const displayName =
    userProfile?.displayName?.trim() ||
    [clerkUser.firstName, clerkUser.lastName].filter(Boolean).join(" ").trim() ||
    clerkUser.primaryEmailAddress?.emailAddress?.split("@")[0] ||
    "";

  return (
    <div className="mb-4 px-4 pt-2 pb-4 border-b bg-white">
      {displayName && (
        <div className="mb-3">
          <p className="text-sm font-medium text-gray-800 truncate">{displayName}</p>
          <a
            href="/account"
            className="text-[11px] text-gray-400 hover:text-blue-600 transition inline-flex items-center gap-1"
          >
            アカウント設定
            <span aria-hidden>→</span>
          </a>
        </div>
      )}
      <PlanLabel
        nazeka_member={userLikeForLabel.nazeka_member}
        user_plan={displayPlan}
        trialActive={userLikeForLabel.trialActive}
        trialEnd={userLikeForLabel.trialEnd}
        fullAccessUntil={userLikeForLabel.fullAccessUntil}
        accessSource={(userProfile as any)?.entitlements?.access_source ?? null}
        variant="sidebar"
      />
      <PlanCtaLink
        source="sidebar"
        className="ml-2 mt-2 text-[11px] text-gray-400 hover:text-blue-600 transition inline-flex items-center gap-1"
      >
        プラン変更
        <span aria-hidden>→</span>
      </PlanCtaLink>
    </div>
  );
}
